import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Grid,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
  Grid2,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import CloseIcon from "@mui/icons-material/Close";
import CategoryIcon from "@mui/icons-material/Category";
import { useNavigate } from "react-router-dom";
import api from "../services/authService";

import PageHeader from "../components/common/PageHeader";
import AddButton from "../components/common/Filters/AddButton";
import SearchFilters from "../components/common/SearchFilters";
import SearchButton from "../components/common/Filters/SearchButton";
import SearchInput from "../components/common/Filters/SearchInput";
import SingleSelectFilter from "../components/common/Filters/SingleSelectFilter";
import CommonPagination from "../components/common/CommonPagination";
import FilterGrid2 from "../components/common/Filters/FilterGrid2";

// Define interfaces for the API data
interface Category {
  _id: string;
  name: string;
  description: string;
  isActive: boolean;
  createdBy: string;
  objectId: string;
  createdAt: string;
  updatedAt: string;
}

interface Knowledge {
  _id: string;
  title: string;
  description: string;
  duration: number;
  category: Category;
  videoUrl1: string;
  videoUrl2?: string;
  status: string;
  isActive: boolean;
  createdBy: string;
  objectId: string;
  createdAt: string;
  updatedAt: string;
}

interface KnowledgeResponse {
  data: Knowledge[];
  meta: {
    page: number;
    perPage: number;
    total: number;
    totalPages: number;
  };
}

interface KnowledgeRow {
  id: string;
  title: string;
  description: string;
  category: string;
  categoryId: string;
  duration: string;
  status: string;
  isActive: boolean;
  createdAt: string;
  _id: string;
  objectId: string;
}

const Learning: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [knowledgeList, setKnowledgeList] = useState<KnowledgeRow[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(isMobile ? 5 : 10);
  const [categories, setCategories] = useState<Category[]>([]);
  const [searchParams, setSearchParams] = useState({
    category: "",
    status: "",
    keyword: "",
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  // Fetch categories for filters
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get("/knowledge-categories");
        setCategories(response.data.data);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchCategories();
  }, []);

  // Fetch knowledge data
  useEffect(() => {
    const fetchKnowledge = async () => {
      setLoading(true);
      try {
        // Call the actual API endpoint
        const response = await api.get("/knowledge", {
          params: {
            page: currentPage,
            perPage: pageSize,
            category: searchParams.category ?? undefined,
            status: searchParams.status ?? undefined,
            search: searchParams.keyword ?? undefined,
          },
        });
        const data: KnowledgeResponse = response.data;

        // Transform data for DataGrid
        const rows = data.data.map((item) => ({
          id: item._id,
          _id: item._id,
          title: item.title,
          description: item.description,
          category: item.category.name,
          categoryId: item.category._id,
          duration: `${item.duration} นาที`,
          status: item.status === "PUBLISHED" ? "เผยแพร่" : "แบบร่าง",
          isActive: item.isActive,
          objectId: item.objectId,
          createdAt: new Date(item.createdAt).toLocaleDateString("th-TH"),
        }));

        setKnowledgeList(rows);
        setTotalItems(data.meta.total);
      } catch (error) {
        console.error("Error fetching knowledge data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchKnowledge();
  }, [currentPage, pageSize, searchParams]);

  const handleAddLesson = () => {
    navigate("/learning/add");
  };

  const handleEditLesson = (id: string) => {
    navigate(`/learning/edit/${id}`);
  };

  const handlePlayLesson = (id: string) => {
    navigate(`/learning/${id}`);
  };

  const handleDeleteClick = (lesson: { id: string; title: string }) => {
    setSelectedLesson(lesson);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedLesson) return;

    try {
      // Call the actual API endpoint to delete knowledge
      await api.delete(`/knowledge/${selectedLesson.id}`);

      // Update the local state to remove the deleted item
      setKnowledgeList((prev) =>
        prev.filter((item) => item.id !== selectedLesson.id)
      );
      setTotalItems((prev) => prev - 1);

      setDeleteDialogOpen(false);
      setSelectedLesson(null);

      // Show success message
      setSnackbar({
        open: true,
        message: "ลบบทเรียนสำเร็จ",
        severity: "success",
      });
    } catch (error) {
      console.error("Error deleting knowledge:", error);

      // Show error message
      setSnackbar({
        open: true,
        message: "เกิดข้อผิดพลาดในการลบบทเรียน",
        severity: "error",
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedLesson(null);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Handle search
  const handleSearch = (searchValue?: string) => {
    console.log("handleSearch searchValue:", searchValue);
    setSearchParams(prev => ({
      ...prev,
      keyword: searchValue || "",
    }));
    setCurrentPage(1); // Reset to first page when searching
  };


  // Handle filter change
  const handleFilterChange = (filterName: string, value: string) => {
    console.log("handleFilterChange filterName:", filterName, "value:", value);
    setSearchParams(prev => {
      const newParams = {
        ...prev,
        [filterName]: value,
      };
      console.log("handleFilterChange newParams:", newParams);
      return newParams;
    });
    setCurrentPage(1); // Reset to first page when filtering
  };

  const columns = [
    {
      field: "id",
      headerName: "ลำดับ",
      width: 80,
      renderCell: (row: KnowledgeRow) => {
        const rowIndex = knowledgeList.findIndex((item) => item.id === row.id);
        return (currentPage - 1) * pageSize + rowIndex + 1;
      },
    },
    { field: "title", headerName: "ชื่อบทเรียน", width: 250 },
    { field: "category", headerName: "หมวดหมู่", width: 150 },
    { field: "duration", headerName: "ระยะเวลา", width: 120 },
    { field: "status", headerName: "สถานะ", width: 150 },
    {
      field: "actions",
      headerName: "จัดการ",
      width: 180,
      renderCell: (row: KnowledgeRow) => (
        <Box>
          <IconButton onClick={() => handleEditLesson(row.objectId)}>
            <Box
              component="img"
              src="/images/icons/button-modify.png"
              alt="View"
              sx={{ width: 32, height: 32 }}
            />
          </IconButton>
          <IconButton
            onClick={() =>
              handleDeleteClick({ id: row.objectId, title: row.title })
            }
          >
            <Box
              component="img"
              src="/images/icons/button-bin.png"
              alt="View"
              sx={{ width: 32, height: 32 }}
            />
          </IconButton>
        </Box>
      ),
    },
  ];

  // Generate search filters dynamically from categories
  const searchFilters = [
    {
      label: "หมวดหมู่",
      value: "category",
      options: [
        ...categories.map((category) => ({
          value: category._id,
          label: category.name,
        })),
      ],
      onChange: (value: string) => handleFilterChange("category", value),
    },
    {
      label: "สถานะ",
      value: "status",
      options: [
        { value: "PUBLISHED", label: "เผยแพร่" },
        { value: "DRAFT", label: "แบบร่าง" },
      ],
      onChange: (value: string) => handleFilterChange("status", value),
    },
  ];

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        maxWidth: "100%",
      }}
    >
      <PageHeader title="บทเรียนออนไลน์" />

      {/* <SearchFilters
        filters={searchFilters}
        searchPlaceholder="ค้นหาบทเรียน"
        onSearch={handleSearch}
        onFilterChange={handleFilterChange}
      /> */}

      <FilterGrid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
          <SearchInput
            title="ค้นหาบทเรียน"
            placeholder="กรุณากรอกคำค้นหา"
            value={searchParams.keyword}
            onChange={(value) => setSearchParams(prev => ({
              ...prev,
              keyword: value,
            }))}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
          <SingleSelectFilter
            filter={searchFilters[0]}
            value={searchParams.category}
            onChange={(e, filter) =>
              handleFilterChange(filter.value, e.target.value)
            }
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
          <SingleSelectFilter
            filter={searchFilters[1]}
            value={searchParams.status}
            onChange={(e, filter) =>
              handleFilterChange(filter.value, e.target.value)
            }
          />
        </Grid2>
        <Grid2>
          <SearchButton 
            onClick={() => handleSearch(searchParams.keyword)}
          />
        </Grid2>
        <Grid2 sx={{ marginLeft: { xs: 0, md: 0, lg: "auto" } }}>
          <AddButton label="เพิ่มบทเรียน" onClick={handleAddLesson} />
        </Grid2>
      </FilterGrid2>

      <Paper sx={{ width: "100%", overflow: "hidden"}}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.field}
                    align={column.field === "actions" ? "center" : "left"}
                    style={{
                      minWidth: column.width,
                    }}
                  >
                    {column.headerName}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : knowledgeList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    ไม่พบข้อมูล
                  </TableCell>
                </TableRow>
              ) : (
                knowledgeList.map((row) => (
                  <TableRow hover key={row.id}>
                    {columns.map((column) => (
                      <TableCell
                        key={column.field}
                        align={column.field === "actions" ? "center" : "left"}
                      >
                        {column.renderCell
                          ? column.renderCell(row)
                          : row[column.field as keyof KnowledgeRow]}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <CommonPagination
        count={Math.ceil(totalItems / pageSize)}
        page={currentPage}
        onChange={(event, value) => handlePageChange(value)}
        onRowsPerPageChange={(value) => handlePageSizeChange(value)}
        totalItems={totalItems}
      />

      {/* Dialog สำหรับยืนยันการลบบทเรียน */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          ท่านต้องการยืนยันการลบบทเรียน ใช่หรือไม่ ?
          <IconButton
            aria-label="close"
            onClick={handleDeleteCancel}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Typography variant="body1">
            คุณต้องการลบบทเรียน "{selectedLesson?.title}" ใช่หรือไม่?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            ยกเลิก
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="primary"
            variant="contained"
          >
            ยืนยัน
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Learning;
